import React, { useState, useEffect, useCallback } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { payLinksAPI, feesAPI, formatApiError } from '../services/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent } from '../components/ui/card';
import { CurrencyDollar, CheckCircle, ArrowLeft, Info } from '@phosphor-icons/react';
import { toast } from 'sonner';

const DARK_LOGO = "https://customer-assets.emergentagent.com/job_moneyswap-9/artifacts/pvoha9c5_D%24.png";

export default function PayLinkPage() {
  const { username } = useParams();
  const [searchParams] = useSearchParams();
  const presetAmount = searchParams.get('amount');
  const presetNote = searchParams.get('note') || '';
  const { user: currentUser, isAuthenticated } = useAuth();

  const [recipient, setRecipient] = useState(null);
  const [amount, setAmount] = useState(presetAmount || '');
  const [note, setNote] = useState(presetNote);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [success, setSuccess] = useState(false);
  const [feeInfo, setFeeInfo] = useState(null);

  useEffect(() => {
    const timer = setTimeout(async () => {
      const parsed = parseFloat(amount);
      if (isNaN(parsed) || parsed <= 0) { setFeeInfo(null); return; }
      try { const res = await feesAPI.calculate(parsed); setFeeInfo(res.data); } catch (err) { console.error('Fee calculation failed:', err); setFeeInfo(null); }
    }, 300);
    return () => clearTimeout(timer);
  }, [amount]);

  const loadRecipient = useCallback(async () => {
    try {
      const res = await payLinksAPI.resolve(username);
      setRecipient(res.data);
    } catch (err) {
      console.error('Failed to resolve pay link recipient:', err);
      setRecipient(null);
    } finally {
      setLoading(false);
    }
  }, [username]);

  useEffect(() => { loadRecipient(); }, [loadRecipient]);

  const handleSend = async () => {
    const parsed = parseFloat(amount);
    if (isNaN(parsed) || parsed <= 0) { toast.error('Enter a valid amount'); return; }
    setSending(true);
    try {
      await payLinksAPI.send(username, parsed, note);
      setSuccess(true);
      toast.success('Payment sent!');
    } catch (error) {
      toast.error(formatApiError(error));
    } finally {
      setSending(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#111111] flex items-center justify-center">
        <div className="animate-pulse text-[#999999]">Loading...</div>
      </div>
    );
  }

  if (!recipient) {
    return (
      <div className="min-h-screen bg-[#111111] flex items-center justify-center p-6">
        <Card className="bg-[#1A1A1A] border border-[#333333] rounded-sm max-w-md w-full">
          <CardContent className="p-8 text-center">
            <CurrencyDollar className="w-16 h-16 mx-auto mb-4 text-[#333333]" />
            <p className="text-white text-lg font-bold mb-2">User Not Found</p>
            <p className="text-[#999999] mb-6">No user with username ${username}</p>
            <Link to="/"><Button className="bg-[#22C55E] hover:bg-[#16A34A] text-white rounded-sm">Home</Button></Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (success) {
    return (
      <div className="min-h-screen bg-[#111111] flex items-center justify-center p-6">
        <Card className="bg-[#1A1A1A] border border-[#333333] rounded-sm max-w-md w-full" data-testid="pay-success-card">
          <CardContent className="p-8 text-center">
            <div className="w-16 h-16 bg-[#22C55E]/10 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="w-10 h-10 text-[#22C55E]" weight="fill" />
            </div>
            <h2 className="text-2xl font-bold text-white mb-2" style={{ fontFamily: 'Cabinet Grotesk' }}>Payment Sent!</h2>
            <p className="text-[#999999] mb-6">${parseFloat(amount).toFixed(2)} sent to ${recipient.username}</p>
            <div className="flex gap-3">
              <Button variant="outline" onClick={() => { setSuccess(false); setAmount(''); setNote(''); }} className="flex-1 rounded-sm border-[#333333] text-white hover:bg-[#333333]" data-testid="send-another-pay">Send Another</Button>
              <Link to="/dashboard" className="flex-1"><Button className="w-full bg-[#22C55E] hover:bg-[#16A34A] text-white rounded-sm">Dashboard</Button></Link>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#111111]">
      <header className="bg-[#111111]/70 backdrop-blur-xl border-b border-[#333333] sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2"><img src={DARK_LOGO} alt="Divispend" className="h-10 w-auto" /></Link>
          {isAuthenticated ? (
            <Link to="/dashboard"><Button variant="ghost" className="text-[#999999] hover:text-white hover:bg-[#333333] rounded-sm">Dashboard</Button></Link>
          ) : (
            <Link to="/login"><Button className="bg-[#22C55E] hover:bg-[#16A34A] text-white rounded-sm">Sign in to pay</Button></Link>
          )}
        </div>
      </header>

      <main className="flex items-center justify-center p-6 min-h-[calc(100vh-73px)]">
        <Card className="w-full max-w-md bg-[#1A1A1A] border border-[#333333] rounded-sm shadow-none" data-testid="pay-link-card">
          <CardContent className="p-8">
            {/* Recipient */}
            <div className="text-center mb-8">
              {recipient.avatar_url ? (
                <img src={recipient.avatar_url} alt={recipient.name} className="w-20 h-20 rounded-full mx-auto mb-4 object-cover border-4 border-[#22C55E]" />
              ) : (
                <div className="w-20 h-20 bg-[#22C55E]/20 rounded-full mx-auto mb-4 flex items-center justify-center text-[#22C55E] text-3xl font-bold">
                  {recipient.name?.[0]?.toUpperCase() || 'U'}
                </div>
              )}
              <h2 className="text-xl font-bold text-white" style={{ fontFamily: 'Cabinet Grotesk' }} data-testid="pay-recipient-name">
                Pay {recipient.name}
              </h2>
              <p className="text-[#22C55E] font-semibold" data-testid="pay-recipient-username">${recipient.username}</p>
              {recipient.bio && <p className="text-sm text-[#999999] mt-2">{recipient.bio}</p>}
            </div>

            {!isAuthenticated ? (
              <div className="text-center">
                <p className="text-[#999999] mb-4">Sign in to send money</p>
                <Link to={`/login?redirect=/pay/${username}${presetAmount ? `?amount=${presetAmount}` : ''}`}>
                  <Button className="w-full bg-[#22C55E] hover:bg-[#16A34A] text-white rounded-sm h-11 font-semibold" data-testid="signin-to-pay">
                    Sign in to Pay
                  </Button>
                </Link>
                <Link to={`/register?redirect=/pay/${username}`}>
                  <Button variant="ghost" className="w-full mt-2 text-[#999999] hover:text-white hover:bg-[#333333] rounded-sm">
                    Create Account
                  </Button>
                </Link>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label className="text-xs tracking-[0.1em] uppercase font-semibold text-[#999999]">Amount</Label>
                  <div className="relative">
                    <span className="absolute left-4 top-1/2 -translate-y-1/2 text-[#999999] text-xl font-bold">$</span>
                    <Input
                      type="number" step="0.01" min="0.01" placeholder="0.00"
                      value={amount} onChange={(e) => setAmount(e.target.value)}
                      className="rounded-sm bg-[#111111] border-[#333333] text-white pl-10 text-2xl font-bold h-14 focus:ring-2 focus:ring-[#22C55E]"
                      autoFocus={!presetAmount}
                      data-testid="pay-amount-input"
                    />
                  </div>
                </div>

                {feeInfo && feeInfo.fee > 0 && (
                  <div className="bg-[#111111] rounded-sm p-3 space-y-1 text-sm" data-testid="pay-fee-breakdown">
                    <div className="flex items-center gap-1 text-[#999999]"><Info className="w-3 h-3" /> Fee: ${feeInfo.fee.toFixed(2)}</div>
                    <div className="flex justify-between font-bold"><span className="text-[#999999]">Total</span><span className="text-[#22C55E]">${feeInfo.sender_pays?.toFixed(2)}</span></div>
                  </div>
                )}

                <div className="space-y-2">
                  <Label className="text-xs tracking-[0.1em] uppercase font-semibold text-[#999999]">Note (Optional)</Label>
                  <Textarea
                    placeholder="What's this for?"
                    value={note} onChange={(e) => setNote(e.target.value)}
                    className="rounded-sm bg-[#111111] border-[#333333] text-white resize-none"
                    rows={2}
                    data-testid="pay-note-input"
                  />
                </div>

                <Button
                  onClick={handleSend}
                  disabled={sending || !amount}
                  className="w-full bg-[#22C55E] hover:bg-[#16A34A] text-white rounded-sm h-12 font-semibold text-lg"
                  data-testid="pay-send-button"
                >
                  {sending ? 'Sending...' : `Pay $${parseFloat(amount || 0).toFixed(2)}`}
                </Button>

                {currentUser?.user_id === recipient.user_id && (
                  <p className="text-xs text-[#F59E0B] text-center">This is your own payment link</p>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      </main>
    </div>
  );
}
